import { useState, useEffect } from 'react';
import axios from 'axios';

const CohortPreference = () => {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState('all');
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchOrgData = async () => {
      if (!selectedOrgId) return;
      try {
        const preferencesResponse = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/getFeedPreferences', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const orgPreferences = preferencesResponse.data.find(
          (p) => p.organization_id === parseInt(selectedOrgId)
        );
        if (orgPreferences) {
          setSelectedCohorts(orgPreferences.cohorts || []);
          setSelectedTimeline(orgPreferences.timeline || 'all');
          setPostCount(orgPreferences.preference_type === 'cohort' ? orgPreferences.post_count || 0 : 0);
        }

        const cohortsResponse = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/cohorts', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          params: { organization_id: selectedOrgId },
        });
        setCohorts(cohortsResponse.data);
      } catch (error) {
        console.error('Error fetching cohorts or preferences:', error);
      }
    };
    fetchOrgData();
  }, [selectedOrgId]);

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setSelectedCohorts([]);
    setPostCount(0);
    setStatusMsg('');
  };

  const handleCohortSelection = (e) => {
    const { value, checked } = e.target;
    const cohortId = parseInt(value);
    setSelectedCohorts((prev) =>
      checked ? [...prev, cohortId] : prev.filter((id) => id !== cohortId)
    );
  };

  const fetchPostCount = async (cohortIds) => {
    if (cohortIds.length === 0) return;
    try {
      const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/posts/count', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: {
          cohort_ids: cohortIds.join(','),
          timeline: selectedTimeline,
        },
      });
      setPostCount(response.data.totalPosts);
    } catch (error) {
      console.error('Error fetching post count:', error);
    }
  };

  const savePreferences = async (updatedCohorts) => {
    if (updatedCohorts.length === 0) return;
    try {
      await axios.post(
        'https://genz-staging.feeltiptop.com/api/social_glass/saveFeedPreferences',
        {
          organization_id: selectedOrgId,
          cohort_ids: updatedCohorts,
          timeline: selectedTimeline,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setStatusMsg('✅ Preferences saved successfully.');
    } catch (error) {
      setStatusMsg('❌ Error saving preferences.');
      console.error('Error saving preferences:', error);
    }
  };

  const handleUpdatePreferences = async () => {
    await savePreferences(selectedCohorts);
    await fetchPostCount(selectedCohorts);
  };

  return (
    <div className="min-h-screen max-w-3xl mx-auto p-6 bg-white rounded-xl shadow space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Feed Preferences</h1>

      <div>
        <label htmlFor="organization" className="block text-sm font-medium font-semibold text-gray-700">
          Select Organization:
        </label>
        <select
          id="organization"
          value={selectedOrgId}
          onChange={handleOrgChange}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">Select an Organization</option>
          {organizations.map((org) => (
            <option key={org.OrganizationID} value={org.OrganizationID}>
              {org.Name}
            </option>
          ))}
        </select>
      </div>

      {selectedOrgId && (
        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Select Cohorts 
          </h2>
          {cohorts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {cohorts.map((cohort) => (
                <label key={cohort.CohortID} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    value={cohort.CohortID}
                    checked={selectedCohorts.includes(cohort.CohortID)}
                    onChange={handleCohortSelection}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">{cohort.Name}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No cohorts found for this organization.</p>
          )}
        </div>
      )}

      <div>
        <label htmlFor="timeline" className="block text-sm font-medium font-semibold text-gray-700">
          Select Timeline:
        </label>
        <select
          id="timeline"
          value={selectedTimeline}
          onChange={(e) => setSelectedTimeline(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="day">Last Day</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {postCount >= 0 && (
        <div className="text-gray-700 font-medium">
          Total Posts for Selected Cohorts: <span className="font-bold">{postCount}</span>
        </div>
      )}

      <button
        onClick={handleUpdatePreferences}
        className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition"
      >
        Update Preferences
      </button>

      {statusMsg && (
        <div className={`text-sm p-2 rounded ${statusMsg.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {statusMsg}
        </div>
      )}
    </div>
  );
};

export default CohortPreference;
